import { Form, FormInstance } from "antd";
import TextArea from "antd/es/input/TextArea";
import Dragger from "antd/es/upload/Dragger";
import CustomInput from "src/components/custom-input/custom-input.component";
import { useState, useEffect } from "react";

interface ArabicFormProps {
  form: FormInstance;
  user: any;
  onImageRemove?: () => void;
}

// Utility function to trim spaces from file names
const trimFileName = (file: File): File => {
  const originalName = file.name;
  const trimmedName = originalName.replace(/\s+/g, ''); // Remove all spaces
  
  if (originalName !== trimmedName) {
    // Create a new File object with the trimmed name
    return new File([file], trimmedName, {
      type: file.type,
      lastModified: file.lastModified,
    });
  }
  
  return file;
};

const ArabicForm = ({ form, user, onImageRemove }: ArabicFormProps) => {
  const [fileList, setFileList] = useState<any[]>([]);

  // Check if there's an existing image URL and create a file list item
  useEffect(() => {
    const imageUrl = form.getFieldValue('imageUrlAr');
    if (imageUrl) {
      setFileList([{
        uid: '-1',
        name: 'image.jpg',
        status: 'done',
        url: imageUrl,
      }]);
    }
  }, [form]);

  return (
    <div className="w-full rounded-xl outline outline-1 outline-offset-[-1px] outline-slate-300 inline-flex flex-col justify-center items-center gap-5">
      <div className="px-5 pt-5 self-stretch inline-flex justify-between items-center">
        <div className="flex justify-start items-center gap-4">
          <div className="flex justify-start items-center gap-1.5">
            <div className="justify-center text-zinc-800 text-base font-medium font-['Montserrat']">
              Message in Arabic
            </div>
          </div>
        </div>
      </div>
      <div className="w-full  rotate-180 flex flex-col justify-start items-start gap-2.5">
        <div className="self-stretch h-px bg-slate-300" />
      </div>
      <div className="px-5 w-full flex flex-col justify-start items-start">
        <div className="self-stretch inline-flex justify-between flex-wrap items-start">
          <div className=" inline-flex w-full lg:w-[68%] flex-col justify-start items-start">
            <div className="self-stretch flex flex-col justify-start items-start gap-2">
              <div className="self-stretch h-6 justify-center text-zinc-800 text-sm font-medium font-['Montserrat']">
                Message title
              </div>
              <div className="flex flex-col w-full">
                <Form.Item
                  name="titleAr"
                  rules={[
                    {
                      required: true,
                      message: "Please input title!",
                    },
                  ]}
                >
                  <TextArea
                    placeholder="Enter notification title "
                    rows={1}
                    className="p-2 border-[1px] outline-none border-slate-300"
                  />
                </Form.Item>
              </div>
            </div>
            <div className="self-stretch flex flex-col justify-start items-start gap-2">
              <div className="self-stretch h-6 justify-center text-zinc-800 text-sm font-medium">
                Message body
              </div>
              <div className="flex flex-col w-full">
                <Form.Item
                  name="bodyAr"
                  rules={[
                    {
                      required: true,
                      message: "Please input body!",
                    },
                  ]}
                >
                  <TextArea
                    placeholder="Enter notification body"
                    rows={6}
                    className="p-2 border-[1px] outline-none border-slate-300"
                  />
                </Form.Item>
              </div>
            </div>
          </div>
          <div className="h-72 w-full lg:w-[30%] inline-flex flex-col justify-start items-start gap-4">
            <div className="h-72 w-full flex flex-col justify-start items-start gap-2">
              <div className="self-stretch h-6 justify-center text-zinc-800 text-sm font-medium font-['Montserrat']">
                Image URL
              </div>
              <div className="flex flex-col w-full">
                <Form.Item name="imageUrlAr">
                  <Dragger
                    action={`${process.env.REACT_APP_BASE_URL}/${process.env.REACT_APP_ENDPOINT_VERSION}/files/upload-image`}
                    name="file"
                    headers={{
                      Authorization: `Bearer ${user?.accessToken}`,
                    }}
                    multiple={false}
                    fileList={fileList}
                    beforeUpload={(file) => {
                      // Trim spaces from file name before upload
                      const trimmedFile = trimFileName(file);
                      return trimmedFile;
                    }}
                    onChange={(info) => {
                      setFileList(info.fileList);
                      if (info.file.status === "done") {
                        form.setFieldsValue({
                          imageUrlAr: info.file.response?.data?.url,
                        });
                      } else if (info.file.status === "removed") {
                        // Clear form value when file is removed
                        form.setFieldsValue({
                          imageUrlAr: "",
                        });
                        // Notify parent component to update saved state
                        if (onImageRemove) {
                          onImageRemove();
                        }
                      }
                    }}
                    onRemove={(file) => {
                      // Clear form value when file is removed
                      form.setFieldsValue({
                        imageUrlAr: "",
                      });
                      // Notify parent component to update saved state
                      if (onImageRemove) {
                        onImageRemove();
                      }
                      return true; // Allow removal
                    }}
                    accept="image/*,.pdf"
                    maxCount={1}
                  >
                    <div className="w-full h-64 flex-1 px-4 py-3 bg-white rounded-lg outline outline-2 outline-offset-[-2px] outline-sky-900 inline-flex justify-center items-center gap-2">
                      <div className="flex-1 inline-flex flex-col justify-center items-center gap-6">
                        <img
                          src="/images/image-drag.svg"
                          alt="upload-image"
                          className="w-12 h-12"
                        />
                        <div className="justify-center text-sky-900 text-sm font-normal font-['Montserrat']">
                          Click or drag to upload file
                        </div>
                      </div>
                    </div>
                  </Dragger>
                </Form.Item>
              </div>
            </div>
          </div>
        </div>
        <div className="self-stretch flex flex-col justify-start items-start gap-2">
          <div className="self-stretch h-6 justify-center text-zinc-800 text-sm font-medium font-['Montserrat']">
            Link
          </div>
          <div className="flex flex-col w-full">
            <Form.Item name="linkAr">
              <CustomInput
                placeholder="Enter notification link"
                className="border-[1px] outline-none border-slate-300"
              />
            </Form.Item>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArabicForm;
